import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/api/api-client.config.ts';
import { QueryConfig } from '@/api/react-query.config.ts';
import { PickemLeaderboardData } from '@/@types/api.types.ts';

type GetPickemLeaderboardProps = {
  pickemId: number;
  offset: number;
  limit: number;
};

const getPickemLeaderboard = ({
  pickemId,
  offset,
  limit,
}: GetPickemLeaderboardProps): Promise<PickemLeaderboardData> =>
  api.get(`/admin/pickem-leaderboard/${pickemId}`, {
    params: {
      offset,
      limit,
    },
  });

const getPickemLeaderboardOptions = ({
  pickemId,
  offset,
  limit,
}: GetPickemLeaderboardProps) => {
  return infiniteQueryOptions({
    queryKey: ['getInfinitePickemLeaderboardOptions', pickemId, offset, limit],
    queryFn: ({ pageParam = 0 }) =>
      getPickemLeaderboard({
        pickemId,
        offset: pageParam * limit,
        limit,
      }),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.leaderboard?.length > 0 ? allPages?.length : undefined;
    },
    initialPageParam: 0,
  });
};

type UseGetPickemLeaderboardProps = GetPickemLeaderboardProps & {
  queryConfig?: QueryConfig<typeof getPickemLeaderboard>;
};

export const useGetPickemLeaderboard = ({
  pickemId,
  offset,
  limit,
}: UseGetPickemLeaderboardProps) => {
  return useInfiniteQuery({
    ...getPickemLeaderboardOptions({
      pickemId,
      offset,
      limit,
    }),
    // Refetch every minute
    refetchInterval: 1000 * 60,
    enabled: Boolean(pickemId),
    select: (data) => {
      // Merge leaderboard data from each page into a single array.
      // Keep all other properties as they are on page 0.
      if (!data?.pages?.length) return null;
      const firstPage = data.pages[0];
      const mergedLeaderboard = data.pages.flatMap(
        (page) => page.leaderboard || []
      );
      return {
        ...firstPage,
        leaderboard: mergedLeaderboard,
      };
    },
  });
};
